/**
 * useDedicatedServerActions — Orchestration des 5 mutations admin
 * (retry, rebuild, destroy, suspend, resume) + force-cancel (TODO backend).
 *
 * Chaque mutation :
 *  - invalide les queries Refine (list + detail) → rafraîchissement auto
 *  - affiche un toast AntD `message.success` / `message.error`
 *  - appelle le callback externe `onSuccess` si fourni
 *
 * @spec specs/app/admin/dedicated-servers.md §7.1
 */

import { useCustomMutation, useInvalidate } from '@refinedev/core';
import { App as AntdApp } from 'antd';
import { useCallback } from 'react';

interface ActionHandlers {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface ActionResult {
  retry: (reason: string, handlers?: ActionHandlers) => void;
  rebuild: (reason: string, handlers?: ActionHandlers) => void;
  destroy: (reason: string, handlers?: ActionHandlers) => void;
  suspend: (reason: string, handlers?: ActionHandlers) => void;
  resume: (reason: string, handlers?: ActionHandlers) => void;
  isRetrying: boolean;
  isRebuilding: boolean;
  isDestroying: boolean;
  isSuspending: boolean;
  isResuming: boolean;
}

type ActionEndpoint = 'retry' | 'rebuild' | 'destroy' | 'suspend' | 'resume';

const SUCCESS_MSG: Record<ActionEndpoint, string> = {
  retry: 'Setup relancé depuis le stage échoué.',
  rebuild: 'Reconstruction des images workspace lancée.',
  destroy: 'Serveur marqué pour destruction.',
  suspend: 'Serveur suspendu.',
  resume: 'Serveur repris.',
};

const ERROR_MSG: Record<ActionEndpoint, string> = {
  retry: 'Retry échoué',
  rebuild: 'Rebuild échoué',
  destroy: 'Destruction échouée',
  suspend: 'Suspension échouée',
  resume: 'Reprise échouée',
};

export function useDedicatedServerActions(serverId: string): ActionResult {
  const { message } = AntdApp.useApp();
  const invalidate = useInvalidate();

  const retryMutation = useCustomMutation();
  const rebuildMutation = useCustomMutation();
  const destroyMutation = useCustomMutation();
  const suspendMutation = useCustomMutation();
  const resumeMutation = useCustomMutation();

  const invalidateAll = useCallback(() => {
    void invalidate({ resource: 'admin/dedicated-servers', invalidates: ['list', 'many'] });
    void invalidate({
      resource: 'admin/dedicated-servers',
      id: serverId,
      invalidates: ['detail'],
    });
  }, [invalidate, serverId]);

  const runAction = useCallback(
    (endpoint: ActionEndpoint, reason: string, handlers?: ActionHandlers) => {
      const mutation =
        endpoint === 'retry'
          ? retryMutation
          : endpoint === 'rebuild'
            ? rebuildMutation
            : endpoint === 'destroy'
              ? destroyMutation
              : endpoint === 'suspend'
                ? suspendMutation
                : resumeMutation;

      mutation.mutate(
        {
          url: `/api/v1/admin/dedicated-servers/${serverId}/${endpoint}`,
          method: 'post',
          values: { reason },
        },
        {
          onSuccess: () => {
            void message.success(SUCCESS_MSG[endpoint]);
            invalidateAll();
            handlers?.onSuccess?.();
          },
          onError: (err) => {
            const detail = err instanceof Error ? err.message : String(err);
            void message.error(`${ERROR_MSG[endpoint]} : ${detail}`);
            handlers?.onError?.(err);
          },
        },
      );
    },
    [
      serverId,
      message,
      invalidateAll,
      retryMutation,
      rebuildMutation,
      destroyMutation,
      suspendMutation,
      resumeMutation,
    ],
  );

  return {
    retry: (reason, handlers) => runAction('retry', reason, handlers),
    rebuild: (reason, handlers) => runAction('rebuild', reason, handlers),
    destroy: (reason, handlers) => runAction('destroy', reason, handlers),
    suspend: (reason, handlers) => runAction('suspend', reason, handlers),
    resume: (reason, handlers) => runAction('resume', reason, handlers),
    isRetrying: retryMutation.isLoading,
    isRebuilding: rebuildMutation.isLoading,
    isDestroying: destroyMutation.isLoading,
    isSuspending: suspendMutation.isLoading,
    isResuming: resumeMutation.isLoading,
  };
}
